import type { PasteFile } from "./api";

function localPath(ref: string): string {
  return ref.replace(/^\.\//, "").replace(/^\/+/, "");
}

function escapeCloseTag(code: string, tag: string): string {
  return code.replace(new RegExp(`</${tag}`, "gi"), `<\\/${tag}`);
}

/**
 * Инлайнит локальные <link rel=stylesheet> и <script src> прямо в HTML,
 * чтобы превью работало без сохранения и без сетевых запросов.
 */
function inlineResources(html: string, map: Map<string, string>): string {
  const withScripts = html.replace(/<script\b([^>]*)>\s*<\/script>/gi, (tag, attrs: string) => {
    const src = /src\s*=\s*["']([^"']+)["']/i.exec(attrs);
    if (!src) return tag;
    const content = map.get(localPath(src[1] ?? ""));
    if (content === undefined) return tag;
    const type = /type\s*=\s*["']([^"']+)["']/i.exec(attrs);
    const typeAttr = type ? ` type="${type[1]}"` : "";
    return `<script${typeAttr}>${escapeCloseTag(content, "script")}</script>`;
  });

  return withScripts.replace(/<link\b([^>]*)>/gi, (tag, attrs: string) => {
    if (!/\brel\s*=\s*["']?stylesheet/i.test(attrs)) return tag;
    const href = /href\s*=\s*["']([^"']+)["']/i.exec(attrs);
    if (!href) return tag;
    const content = map.get(localPath(href[1] ?? ""));
    if (content === undefined) return tag;
    return `<style>${escapeCloseTag(content, "style")}</style>`;
  });
}

/**
 * Скрипт-мост для execute_js: слушает сообщения от приложения, выполняет код
 * в этом же документе и отправляет результат наверх.
 *
 * Живёт внутри sandbox-iframe на uc-origin (opaque origin): доступа к
 * cookie/localStorage приложения у него нет.
 */
function evalBridge(token: string, renderId: string): string {
  return `<script>(function(){
  var TOKEN = ${JSON.stringify(token)};
  var RENDER_ID = ${JSON.stringify(renderId)};
  var MAX_LOG = 200;
  var LOG = [];

  // --- сбор ошибок консоли ---------------------------------------------
  function stringify(a) {
    if (typeof a === "string") return a;
    if (a instanceof Error) return String(a.stack || a);
    try { var s = JSON.stringify(a); return s === undefined ? String(a) : s; }
    catch (e) { return String(a); }
  }
  function push(level, args) {
    try {
      var text = Array.prototype.map.call(args, stringify).join(" ");
      if (text.length > 2000) text = text.slice(0, 2000) + "…";
      LOG.push({ level: level, text: text, ts: Date.now() });
      if (LOG.length > MAX_LOG) LOG.shift();
    } catch (e) {}
  }
  ["error", "warn"].forEach(function (level) {
    var orig = console[level];
    console[level] = function () { push(level, arguments); return orig.apply(console, arguments); };
  });
  window.addEventListener("error", function (e) {
    var where = e.filename ? " (" + e.filename + ":" + e.lineno + ")" : "";
    var msg = e.message || "error";
    // В части браузеров message уже начинается с "Uncaught".
    if (msg.indexOf("Uncaught") !== 0) msg = "Uncaught " + msg;
    push("error", [msg + where]);
  });
  window.addEventListener("unhandledrejection", function (e) {
    push("error", ["Unhandled rejection: " + stringify(e.reason)]);
  });

  // --- рукопожатие готовности -------------------------------------------
  function ready() {
    try { window.top.postMessage({ type: "vetchka:eval-ready", token: TOKEN, renderId: RENDER_ID }, "*"); } catch (e) {}
  }
  if (document.readyState === "complete") ready();
  else window.addEventListener("load", ready);

  function reply(type, id, payload) {
    try {
      var msg = { type: type, token: TOKEN, id: id };
      for (var k in payload) msg[k] = payload[k];
      window.top.postMessage(msg, "*");
    } catch (e) {}
  }
  function safe(v) {
    try { var s = JSON.stringify(v); return s === undefined ? String(v) : s; }
    catch (e) { return String(v); }
  }

  window.addEventListener("message", function (event) {
    var d = event.data;
    if (!d) return;

    // --- консоль: отдаём накопленные записи ------------------------------
    if (d.type === "vetchka:console-get") {
      if (d.token !== TOKEN) return;
      var n = typeof d.limit === "number" ? d.limit : 50;
      reply("vetchka:console-result", d.id, { entries: LOG.slice(-n) });
      return;
    }

    if (d.type !== "vetchka:eval" || d.token !== TOKEN) return;

    // Выполняем в теле async-функции: можно использовать await и return,
    // а объявления переменных не конфликтуют с глобальными const страницы.
    var fn;
    var NL = String.fromCharCode(10);
    try {
      fn = new Function("return (async function(){" + NL + d.code + NL + "})()");
    } catch (err) {
      reply("vetchka:eval-result", d.id, { ok: false, error: "SyntaxError: " + String((err && err.message) || err) });
      return;
    }

    var result;
    try { result = fn(); }
    catch (err) {
      reply("vetchka:eval-result", d.id, { ok: false, error: String((err && err.stack) || err) });
      return;
    }

    Promise.resolve(result).then(
      function (v) { reply("vetchka:eval-result", d.id, { ok: true, value: safe(v) }); },
      function (err) { reply("vetchka:eval-result", d.id, { ok: false, error: String((err && err.stack) || err) }); }
    );
  });
})();<\/script>`;
}

/**
 * Вставляет мост как можно раньше — сразу после <head>, чтобы перехватить
 * ошибки, возникающие ещё до пользовательских скриптов в body.
 */
function appendBridge(html: string, bridge: string): string {
  if (bridge === "") return html;
  const head = /<head[^>]*>/i.exec(html);
  if (head) {
    const at = head.index + head[0].length;
    return html.slice(0, at) + bridge + html.slice(at);
  }
  const htmlTag = /<html[^>]*>/i.exec(html);
  if (htmlTag) {
    const at = htmlTag.index + htmlTag[0].length;
    return html.slice(0, at) + bridge + html.slice(at);
  }
  return bridge + html;
}

/** Собирает превью: index.html как есть (с инлайном) либо синтетическую страницу. */
export interface PreviewOptions {
  /** Включает мост execute_js и привязывает ответы к окну приложения. */
  evalToken?: string;
  /** Идентификатор рендера: по нему ждём готовности страницы перед eval. */
  renderId?: string;
}

export function buildPreviewHtml(files: PasteFile[], opts: PreviewOptions = {}): string {
  const map = new Map(files.map((f) => [f.path, f.content]));

  const bridge = opts.evalToken
    ? evalBridge(opts.evalToken, opts.renderId ?? "")
    : "";

  const index = map.get("index.html");
  if (index !== undefined) return appendBridge(inlineResources(index, map), bridge);

  const css = [map.get("style.css"), map.get("styles.css")].filter(Boolean).join("\n");
  const js = ["main.js", "script.js", "index.js", "app.js"]
    .map((p) => map.get(p))
    .filter((v): v is string => v !== undefined)
    .join("\n;\n");
  const fragment = map.get("fragment.html") ?? "<div id='app'></div>";

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<style>${css}</style>
</head>
<body>
${fragment}
<script>${escapeCloseTag(js, "script")}</script>
${bridge}
</body>
</html>`;
}